import { useEffect, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plug, RefreshCw, Save } from "lucide-react";

const DEFAULT_SENTINEL = "__default__";

export default function XeroCard() {
  const [st, setSt] = useState(null);
  const [services, setServices] = useState([]);
  const [mapping, setMapping] = useState({ default_code: "200", map: {} });
  const [accounts, setAccounts] = useState([]);
  const [trackingCats, setTrackingCats] = useState([]);

  const load = () => api.get("/xero/status").then((r) => setSt(r.data)).catch(() => setSt(null));
  const loadAccounts = () => api.get("/xero/accounts").then((r) => setAccounts(r.data || [])).catch(() => setAccounts([]));
  const loadTracking = () => api.get("/xero/tracking-categories").then((r) => setTrackingCats(r.data || [])).catch(() => setTrackingCats([]));
  useEffect(() => {
    load();
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/xero/mapping").then((r) => setMapping(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (st?.connected) { loadAccounts(); loadTracking(); } }, [st?.connected]);

  const saveSalesSourceCat = async (cat) => {
    try {
      const { data } = await api.put("/xero/sales-source-category", { category: cat === "__none__" ? "" : cat });
      setSt((s) => ({ ...s, sales_source_category: data.sales_source_category }));
      toast.success("Sales source category saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save sales source category"); }
  };

  const [vat, setVat] = useState("20");
  const [themes, setThemes] = useState([]);
  const [branding, setBranding] = useState(DEFAULT_SENTINEL);
  const [terms, setTerms] = useState("");
  useEffect(() => { if (st?.vat_rate != null) setVat(String(Math.round(st.vat_rate * 100))); }, [st?.vat_rate]);
  useEffect(() => { setBranding(st?.branding_theme_id || DEFAULT_SENTINEL); setTerms(st?.payment_terms_days != null ? String(st.payment_terms_days) : ""); }, [st?.branding_theme_id, st?.payment_terms_days]);
  useEffect(() => { if (st?.connected) api.get("/xero/branding-themes").then((r) => setThemes(r.data || [])).catch(() => setThemes([])); }, [st?.connected]);
  const testConn = async () => { try { const { data } = await api.get("/xero/test"); toast.success(`Connected to ${data.organisation}`); } catch (e) { toast.error(e.response?.data?.detail || "Test connection failed"); } };
  const saveVat = async () => { try { const { data } = await api.put("/xero/settings", { vat_rate: Number(vat) / 100 }); setSt(data); toast.success("Default VAT rate saved"); } catch { toast.error("Could not save VAT rate"); } };
  const saveBranding = async (v) => { setBranding(v); try { const { data } = await api.put("/xero/settings", { branding_theme_id: v === DEFAULT_SENTINEL ? null : v }); setSt(data); toast.success("Branding theme saved"); } catch { toast.error("Could not save branding theme"); } };
  const saveTerms = async () => { try { const { data } = await api.put("/xero/settings", { payment_terms_days: terms === "" ? null : Number(terms) }); setSt(data); toast.success("Payment terms saved"); } catch { toast.error("Could not save payment terms"); } };
  const connect = async () => {
    try { const { data } = await api.get("/xero/connect"); window.location.assign(data.authorization_url); }
    catch (e) { toast.error(e.response?.data?.detail || "Xero is not configured yet"); }
  };
  const disconnect = async () => { await api.post("/xero/disconnect"); toast.success("Xero disconnected"); setAccounts([]); load(); };
  const toggleAuto = async (v) => {
    const { data } = await api.put("/xero/auto-sync", { enabled: v });
    setSt((s) => ({ ...s, auto_sync: data.auto_sync }));
    toast.success(`Auto-sync ${data.auto_sync ? "enabled" : "disabled"}`);
  };
  const toggleApproved = async (v) => {
    const { data } = await api.put("/xero/repeating-approved", { enabled: v });
    setSt((s) => ({ ...s, repeating_approved: data.repeating_approved }));
    toast.success(`Monthly invoices will ${data.repeating_approved ? "auto-approve & send" : "be created as drafts"}`);
  };
  const setCode = (sid, code) => setMapping((m) => ({ ...m, map: { ...(m.map || {}), [sid]: code } }));
  const toggleSep = (sid) => setMapping((m) => { const set = new Set(m.separate_ids || []); set.has(sid) ? set.delete(sid) : set.add(sid); return { ...m, separate_ids: [...set] }; });
  const setLabel = (code, val) => setMapping((m) => ({ ...m, code_labels: { ...(m.code_labels || {}), [code]: val } }));
  const saveMapping = async () => {
    const { data } = await api.put("/xero/mapping", mapping);
    setMapping(data);
    toast.success("Account mapping saved");
  };

  const optsFor = (code) => (code && !accounts.some((a) => a.code === code)
    ? [{ code, name: "(saved code)", type: "" }, ...accounts] : accounts);
  const hasAccounts = accounts.length > 0;

  if (!st) return null;
  return (
    <div className="max-w-2xl space-y-4">
      <Card className="p-6 rounded-md space-y-4" data-testid="xero-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-alt font-semibold text-cass-navy flex items-center gap-2"><Plug className="w-4 h-4" />Xero</h3>
            <p className="text-xs text-cass-slate mt-1">When a proposal is accepted, sync the client to Xero as a Contact and create a <b>DRAFT monthly repeating invoice</b> for the agreed recurring services (never authorised or auto-sent). It appears under <b>Sales &rarr; Invoices &rarr; Repeating</b> in Xero.</p>
          </div>
          {st.connected
            ? <Badge className="bg-emerald-100 text-emerald-700" data-testid="xero-status-badge">Connected</Badge>
            : <Badge variant="outline" data-testid="xero-status-badge">{st.configured ? "Not connected" : "Not configured"}</Badge>}
        </div>
        {st.connected && <div className="text-sm text-cass-navy" data-testid="xero-tenant">Organisation: <b>{st.tenant_name || "Xero"}</b></div>}
        {st.connected && st.last_connected && <div className="text-xs text-cass-slate" data-testid="xero-last-connected">Last successful connection: {new Date(st.last_connected).toLocaleString("en-GB")}</div>}
        {!st.configured && (
          <div className="text-xs text-cass-slate bg-cass-bone rounded p-3 space-y-1">
            <p>To activate: add your Xero app <b>Client ID / Secret</b> to the backend, then register this exact redirect URI in your Xero app:</p>
            <code className="block break-all text-cass-navy" data-testid="xero-redirect-uri">{st.redirect_uri}</code>
          </div>
        )}
        {st.connected && (
          <div className="flex items-center justify-between border-t border-border pt-3">
            <div>
              <div className="text-sm text-cass-navy font-medium">Auto-sync on acceptance</div>
              <div className="text-xs text-cass-slate">Automatically create the contact + draft repeating invoice when a proposal is accepted.</div>
            </div>
            <Switch checked={!!st.auto_sync} onCheckedChange={toggleAuto} data-testid="xero-autosync-switch" />
          </div>
        )}
        {st.connected && (
          <div className="flex items-center justify-between border-t border-border pt-3">
            <div className="pr-4">
              <div className="text-sm text-cass-navy font-medium">Approve &amp; auto-send monthly invoices</div>
              <div className="text-xs text-cass-slate">On: repeating invoices post as Approved and Xero emails them on the 1st. Off: they post as drafts for you to review first.</div>
            </div>
            <Switch checked={st.repeating_approved !== false} onCheckedChange={toggleApproved} data-testid="xero-approved-switch" />
          </div>
        )}
        {st.connected && (
          <div className="flex items-center justify-between border-t border-border pt-3">
            <div className="pr-4">
              <div className="text-sm text-cass-navy font-medium">Sales source (Xero tracking category)</div>
              <div className="text-xs text-cass-slate">Pick the Xero tracking category used as the "Sales Source". Each client's chosen option is applied to their invoice lines.</div>
            </div>
            <div className="w-52 shrink-0">
              <Select value={st.sales_source_category || "__none__"} onValueChange={saveSalesSourceCat}>
                <SelectTrigger data-testid="xero-salessource-category"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None (don't track)</SelectItem>
                  {trackingCats.map((t) => <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {st.sales_source_category && trackingCats.length > 0 && !trackingCats.some((t) => t.name === st.sales_source_category) && (
                <p className="text-[11px] text-red-500 mt-1" data-testid="xero-ss-warning">&#9888; "{st.sales_source_category}" no longer exists in Xero — pick another.</p>
              )}
            </div>
          </div>
        )}
        {st.connected && (
          <div className="flex items-center justify-between border-t border-border pt-3">
            <div className="pr-4">
              <div className="text-sm text-cass-navy font-medium">Default VAT rate</div>
              <div className="text-xs text-cass-slate">Used for the invoice VAT breakdown shown on the review screen.</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Input type="number" className="w-20" value={vat} onChange={(e) => setVat(e.target.value)} data-testid="xero-vat-rate" /><span className="text-sm text-cass-slate">%</span>
              <Button size="sm" variant="outline" onClick={saveVat} data-testid="xero-vat-save"><Save className="w-3.5 h-3.5 mr-1" />Save</Button>
            </div>
          </div>
        )}
        {st.connected && (
          <div className="flex items-center justify-between border-t border-border pt-3">
            <div className="pr-4">
              <div className="text-sm text-cass-navy font-medium">Invoice branding theme</div>
              <div className="text-xs text-cass-slate">Xero branding theme applied to new repeating &amp; one-off invoices.</div>
            </div>
            <div className="w-52 shrink-0">
              <Select value={branding} onValueChange={saveBranding}>
                <SelectTrigger data-testid="xero-branding-theme"><SelectValue placeholder="Xero default" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={DEFAULT_SENTINEL}>Xero default</SelectItem>
                  {themes.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        {st.connected && (
          <div className="flex items-center justify-between border-t border-border pt-3">
            <div className="pr-4">
              <div className="text-sm text-cass-navy font-medium">Payment terms (days)</div>
              <div className="text-xs text-cass-slate">One-off draft invoice due date = invoice date + this many days.</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Input type="number" className="w-20" value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="e.g. 14" data-testid="xero-terms-days" />
              <Button size="sm" variant="outline" onClick={saveTerms} data-testid="xero-terms-save"><Save className="w-3.5 h-3.5 mr-1" />Save</Button>
            </div>
          </div>
        )}
        <div className="flex gap-2">
          {!st.connected && <Button className="bg-cass-navy text-white" onClick={connect} disabled={!st.configured} data-testid="xero-connect-btn"><Plug className="w-4 h-4 mr-1" />Connect Xero</Button>}
          {st.connected && <Button variant="outline" onClick={disconnect} data-testid="xero-disconnect-btn">Disconnect</Button>}
          {st.connected && <Button variant="outline" onClick={testConn} data-testid="xero-test-btn"><Plug className="w-4 h-4 mr-1" />Test connection</Button>}
          <Button variant="ghost" onClick={() => { load(); if (st.connected) loadAccounts(); }} data-testid="xero-refresh-btn"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
        </div>
      </Card>

      <Card className="p-6 rounded-md space-y-3" data-testid="xero-mapping-card">
        <div>
          <h3 className="font-alt font-semibold text-cass-navy">Sales account mapping</h3>
          <p className="text-xs text-cass-slate mt-1">
            {hasAccounts
              ? "Pick the Xero revenue/sales account each service posts to. Leave a service on \u201cUse default\u201d to use the default account."
              : "Connect Xero to load your chart of accounts, then pick a revenue account per service. (Free-text codes for now.)"}
          </p>
        </div>

        <div className="w-72">
          <Label className="text-xs">Default account</Label>
          {hasAccounts ? (
            <Select value={mapping.default_code || ""} onValueChange={(v) => setMapping({ ...mapping, default_code: v })}>
              <SelectTrigger data-testid="xero-default-code"><SelectValue placeholder="Select account" /></SelectTrigger>
              <SelectContent>
                {optsFor(mapping.default_code).map((a) => (
                  <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input value={mapping.default_code || ""} onChange={(e) => setMapping({ ...mapping, default_code: e.target.value })} data-testid="xero-default-code" />
          )}
        </div>

        <p className="text-[11px] text-cass-slate">Monthly services are <b>consolidated by account code</b> — services sharing an account code combine into one repeating-invoice line. Tick <b>Sep</b> to instead give a service its <b>own individual line</b>. Ignored for one-off services.</p>
        <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
          {services.map((s) => {
            const cur = mapping.map?.[s.id] || "";
            return (
              <div key={s.id} className="grid grid-cols-[auto_1fr_16rem] gap-2 items-center" data-testid={`xero-map-${s.id}`}>
                <label className="flex items-center gap-1 text-[11px] text-cass-slate cursor-pointer" title="Separate line on the monthly repeating invoice">
                  <Checkbox checked={(mapping.separate_ids || []).includes(s.id)} onCheckedChange={() => toggleSep(s.id)} data-testid={`xero-sep-${s.id}`} />Sep
                </label>
                <span className="text-sm text-cass-navy truncate">{s.client_name}</span>
                {hasAccounts ? (
                  <Select value={cur || DEFAULT_SENTINEL} onValueChange={(v) => setCode(s.id, v === DEFAULT_SENTINEL ? "" : v)}>
                    <SelectTrigger data-testid={`xero-code-${s.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={DEFAULT_SENTINEL}>Use default ({mapping.default_code})</SelectItem>
                      {optsFor(cur).map((a) => (
                        <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={cur} onChange={(e) => setCode(s.id, e.target.value)} placeholder={mapping.default_code || "default"} data-testid={`xero-code-${s.id}`} />
                )}
              </div>
            );
          })}
        </div>

        <div className="border-t border-border pt-3 space-y-1.5">
          <p className="text-xs font-semibold text-cass-navy">Consolidated line labels (optional)</p>
          <p className="text-[11px] text-cass-slate">Give an account code a friendly description for its consolidated repeating-invoice line. Blank = default "Monthly Outsourced Finance Function as per proposal {'{proposal}'} for the [Month] [Year].".</p>
          {Array.from(new Set([mapping.default_code, ...Object.values(mapping.map || {})].filter(Boolean))).map((code) => (
            <div key={code} className="grid grid-cols-[8rem_1fr] gap-2 items-center" data-testid={`xero-label-row-${code}`}>
              <span className="text-xs text-cass-slate truncate">Account {code}</span>
              <Input value={mapping.code_labels?.[code] || ""} onChange={(e) => setLabel(code, e.target.value)} placeholder="e.g. Bookkeeping &amp; Payroll" data-testid={`xero-label-${code}`} />
            </div>
          ))}
        </div>

        <Button className="bg-cass-navy text-white" onClick={saveMapping} data-testid="xero-save-mapping-btn"><Save className="w-4 h-4 mr-1" />Save mapping</Button>
      </Card>
    </div>
  );
}
